import { useMutation, useQueryClient } from "@tanstack/react-query"
import React, { useState } from "react"

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from "@atlant/ui"

import { FormOperatorSelect } from "#components/form-query/form-operator-select.tsx"
import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { useAircraftSource } from "#pages/authenticated/fleet/[aircraftId]/use-aircraft-source.ts"
import { client } from "#utils/graphql-client.ts"

const AircraftAddOperatorDocument = graphql(/* GraphQL */ `
  mutation AircraftAddOperator($input: MutationAircraftAddOperatorInput!) {
    aircraftAddOperator(input: $input)
  }
`)

interface AssignOperatorModalProps {
  aircraftId: string
  trigger?: React.ReactNode
}

export const AssignOperatorModal = ({
  aircraftId,
  trigger,
}: AssignOperatorModalProps) => {
  const [open, setOpen] = useState(false)
  const [operatorId, setOperatorId] = useState("")
  const queryClient = useQueryClient()
  const source = useAircraftSource()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () =>
      client.request(AircraftAddOperatorDocument, {
        input: { aircraftId, operatorId, source },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.detail(aircraftId),
      })
      setOperatorId("")
      setOpen(false)
    },
  })

  const handleSubmit = () => {
    if (!operatorId) return
    toast.promise(mutateAsync() as Promise<unknown>, {
      loading: "Assigning operator...",
      success: "Operator assigned!",
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) setOperatorId("")
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button
            variant="outline"
            size="sm"
            className="border-primary/30 text-primary hover:bg-primary/5 mt-1 h-7"
          >
            Assign Operator
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Operator</DialogTitle>
          <DialogDescription>
            Select an operator to manage this aircraft
          </DialogDescription>
        </DialogHeader>

        <FormOperatorSelect
          value={operatorId}
          onChange={setOperatorId}
          label="Operator"
          placeholder="Select operator"
        />

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" className="h-10 flex-1">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={!operatorId || isPending}
            className="h-10 flex-1"
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
